import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSinkState } from "context/sink";
import Survey from "models/Survey";
import { useTags } from "hooks/useTags";

export function useFilteredSurveys() {
  const { surveys, surveyListFilters, sink } = useSinkState();
  const { tags } = useTags();
  const params = useParams<{ id?: string }>();

  const folderFilteredSurveys = useMemo(() => {
    if (!surveys || !Array.isArray(surveys)) return [];
    // When explicit folder filters are set, don't pre-filter by route —
    // let byFolders() handle it with OR logic across all selected folders
    if (surveyListFilters.selectedFolderIds?.length > 0) {
      return surveys;
    }
    if (params.id && params.id !== "all") {
      return surveys.filter((s) => s.folder_id === parseInt(params.id!, 10));
    }
    return surveys;
  }, [surveys, params.id, surveyListFilters.selectedFolderIds]);

  const filteredSurveys = useMemo(() => {
    return Survey.byAllFilters(folderFilteredSurveys, {
      ...surveyListFilters,
      tags,
      orders: sink?.orders ?? [],
    });
  }, [folderFilteredSurveys, surveyListFilters, tags, sink?.orders]);

  const hasActiveFilters = useMemo(() => {
    const {
      searchString,
      selectedTagIds,
      selectedFolderIds,
      selectedSiteIds,
      selectedStatuses,
      selectedOrderTypes,
      dateRange,
      selectedCreatedByUserIds,
      selectedLockedByUserIds,
      showOnlyArchived,
    } = surveyListFilters;
    return !!(
      searchString ||
      (selectedTagIds?.length ?? 0) > 0 ||
      (selectedFolderIds?.length ?? 0) > 0 ||
      (selectedSiteIds?.length ?? 0) > 0 ||
      (selectedStatuses?.length ?? 0) > 0 ||
      (selectedOrderTypes?.length ?? 0) > 0 ||
      (dateRange?.startDate && dateRange?.endDate) ||
      (selectedCreatedByUserIds?.length ?? 0) > 0 ||
      (selectedLockedByUserIds?.length ?? 0) > 0 ||
      showOnlyArchived
    );
  }, [surveyListFilters]);

  return { surveys, folderFilteredSurveys, filteredSurveys, hasActiveFilters };
}
